import * as React from 'react';
import userEvent from '@testing-library/user-event';

import { createRendererWithUniDriver } from '../../test/utils';
import { datePickerInputDriverFactory } from './DatePickerInput.driver';
import { DatePickerInput } from '.';

const getFocused = () => document.querySelector(':focus');

describe('DatePickerInput', () => {
  const createDriver = createRendererWithUniDriver(
    datePickerInputDriverFactory,
  );
  const noop = () => {};

  it('should render', async () => {
    const { driver } = createDriver(
      <DatePickerInput value={new Date()} onChange={() => {}} />,
    );

    expect(await driver.exists()).toBe(true);
    expect(await driver.isLabelExists()).toBe(false);
  });

  it('should render label', async () => {
    const { driver } = createDriver(
      <DatePickerInput value={new Date()} onChange={noop} label="Start Date" />,
    );

    expect(await driver.isLabelExists()).toBe(true);
    expect(await driver.getLabelText()).toBe('Start Date');
  });

  describe('when **not** using combobox pattern', () => {
    it('should calendar be not open', async () => {
      const { driver } = createDriver(
        <DatePickerInput value={new Date()} onChange={noop} />,
      );

      expect(await driver.calendar.exists()).toBe(false);
      expect(await driver.textField.input.isWithoutRole()).toBe(true);
    });

    it('should calendar be open on click on input', async () => {
      const { driver } = createDriver(
        <DatePickerInput value={new Date()} onChange={noop} />,
      );

      await driver.openCalendarWindow();

      expect(await driver.calendar.exists()).toBe(true);
      expect(await driver.textField.input.isFocus()).toBe(true);
      expect(await driver.textField.input.isWithoutRole()).toBe(true);
    });

    it('should input and all suffixes be disabled', async () => {
      const { driver } = createDriver(
        <DatePickerInput value={new Date()} onChange={noop} disabled />,
      );

      const icons = await driver.textField.icons();

      expect(await driver.textField.input.attr('disabled')).toBe('');
      for (const icon of icons) {
        expect(await icon._base.attr('disabled')).toBe('');
      }
    });

    describe('keyboard navigation with Tab', () => {
      it('should not open calendar on Tabing with keyboard by default', async () => {
        const { driver } = createDriver(
          <DatePickerInput value={new Date()} onChange={noop} />,
        );

        expect(getFocused()).toBe(null);

        await userEvent.tab();

        expect(await driver.textField.input.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(false);
      });

      it('should calendar open and close on Tabing with keyboard when autoOpen', async () => {
        const { driver } = createDriver(
          <DatePickerInput value={new Date()} onChange={noop} autoOpen />,
        );

        expect(getFocused()).toBe(null);

        await userEvent.tab();

        expect(await driver.textField.input.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(true);

        await userEvent.tab();

        const firstSuffixButton = (await driver.textField.icons())[0];

        expect(await firstSuffixButton.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(false);

        await userEvent.tab({ shift: true });

        expect(await driver.textField.input.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(true);

        await userEvent.tab();

        expect(await firstSuffixButton.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(false);
      });
    });
  });

  describe('when using combobox pattern', () => {
    it('should calendar be not open', async () => {
      const { driver } = createDriver(
        <DatePickerInput value={new Date()} onChange={noop} isCombobox />,
      );

      expect(await driver.calendar.exists()).toBe(false);
      expect(await driver.textField.input.isCombobox(false)).toBe(true);
    });

    it('should calendar be open on click on input', async () => {
      const { driver } = createDriver(
        <DatePickerInput value={new Date()} onChange={noop} isCombobox />,
      );

      await driver.openCalendarWindow();

      expect(await driver.calendar.exists()).toBe(true);
      expect(await driver.textField.input.isFocus()).toBe(true);
      expect(await driver.textField.input.isCombobox(true)).toBe(true);
    });

    it('should input and all suffixes be disabled', async () => {
      const { driver } = createDriver(
        <DatePickerInput
          value={new Date()}
          onChange={noop}
          disabled
          isCombobox
        />,
      );

      const icons = await driver.textField.icons();

      expect(await driver.textField.input.attr('disabled')).toBe('');
      for (const icon of icons) {
        expect(await icon._base.attr('disabled')).toBe('');
      }
    });

    describe('keyboard navigation with Tab', () => {
      it('should not open calendar on Tabing with keyboard by default', async () => {
        const { driver } = createDriver(
          <DatePickerInput value={new Date()} onChange={noop} isCombobox />,
        );

        expect(getFocused()).toBe(null);

        await userEvent.tab();

        expect(await driver.textField.input.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(false);
      });

      it('should calendar open and close on Tabing with keyboard when autoOpen', async () => {
        const { driver } = createDriver(
          <DatePickerInput
            value={new Date()}
            onChange={noop}
            autoOpen
            isCombobox
          />,
        );

        expect(getFocused()).toBe(null);

        await userEvent.tab();

        expect(await driver.textField.input.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(true);

        await userEvent.tab();

        const firstSuffixButton = (await driver.textField.icons())[0];

        expect(await firstSuffixButton.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(false);

        await userEvent.tab({ shift: true });

        expect(await driver.textField.input.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(true);

        await userEvent.tab();

        expect(await firstSuffixButton.isFocus()).toBe(true);
        expect(await driver.calendar.exists()).toBe(false);
      });
    });
  });

  it('should open & close DatePicker programmatically', async () => {
    const ref = React.createRef<DatePickerInput>();
    const { driver } = createDriver(
      <DatePickerInput
        value={new Date()}
        onChange={noop}
        isCombobox
        ref={ref}
      />,
    );
    expect(await driver.calendar.exists()).toBe(false);

    ref.current.openCalendar();

    expect(await driver.calendar.exists()).toBe(true);

    ref.current.closeCalendar();

    expect(await driver.calendar.exists()).toBe(false);
  });
});
